import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { componentWithFG } from '@confluence/feature-gate-component';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';

interface LoomScriptReadTimeContextProps {
	isLoomScriptReadTimeButtonNudgeShown: boolean;
	isLoomScriptReadTimeSpotlightShown: boolean;
	hideLoomScriptReadTimeButtonNudge: () => void;
	dismissLoomScriptReadTimeSpotlight: () => void;
}

const LoomScriptReadTimeContext = createContext<LoomScriptReadTimeContextProps>({
	isLoomScriptReadTimeButtonNudgeShown: false,
	isLoomScriptReadTimeSpotlightShown: false,
	hideLoomScriptReadTimeButtonNudge: () => {},
	dismissLoomScriptReadTimeSpotlight: () => {},
});

export const useLoomScriptReadTimeContext = () => useContext(LoomScriptReadTimeContext);

interface LoomScriptReadTimeContextProviderProps {
	isEligibleForLoomAIScriptSection: boolean;
	children: React.ReactNode;
}

/**
 * Provides a context for the Loom Script Read Time Button Nudge and Spotlight.
 *
 * Nudge is shown if the page meets the criteria (readtime, is page creator), and the spotlight has not already been dismissed
 * Nudge is hidden when it is clicked on, and the spotlight shown in the dropdown
 * When the spotlight is dismissed, mark it as dismissed in local storage, so the nudge will not show again
 */
export const LoomScriptReadTimeContextProvider = componentWithFG(
	'loom_ai_script_for_confluence_page_killswitch',
	({ isEligibleForLoomAIScriptSection, children }: LoomScriptReadTimeContextProviderProps) => {
		const [isLoomScriptReadTimeButtonNudgeShown, setIsLoomScriptReadTimeButtonNudgeShown] =
			useState(false);

		const [isLoomScriptReadTimeSpotlightShown, setIsLoomScriptReadTimeSpotlightShown] =
			useState(false);

		useEffect(() => {
			if (isEligibleForLoomAIScriptSection) {
				const hasDismissedLoomScriptReadTimeButtonNudge = localStorage.getItemAsBoolean(
					PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_SCRIPT_READTIME_BUTTON_NUDGE_DISMISSED,
				);
				// show the spotlight and nudge if we are eligible and the spotlight has not been dismissed
				setIsLoomScriptReadTimeSpotlightShown(!hasDismissedLoomScriptReadTimeButtonNudge);
				setIsLoomScriptReadTimeButtonNudgeShown(!hasDismissedLoomScriptReadTimeButtonNudge);
			}
		}, [isEligibleForLoomAIScriptSection]);

		const contextValue = useMemo(
			() => ({
				isLoomScriptReadTimeButtonNudgeShown,
				isLoomScriptReadTimeSpotlightShown,
				hideLoomScriptReadTimeButtonNudge: () => setIsLoomScriptReadTimeButtonNudgeShown(false),
				dismissLoomScriptReadTimeSpotlight: () => {
					setIsLoomScriptReadTimeSpotlightShown(false);
					localStorage.setItem(
						PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_SCRIPT_READTIME_BUTTON_NUDGE_DISMISSED,
						'true',
					);
				},
			}),
			[isLoomScriptReadTimeButtonNudgeShown, isLoomScriptReadTimeSpotlightShown],
		);

		return isEligibleForLoomAIScriptSection ? (
			<LoomScriptReadTimeContext.Provider value={contextValue}>
				{children}
			</LoomScriptReadTimeContext.Provider>
		) : (
			children
		);
	},
	// if our killswitch is off, just render the children without looking up local storage
	({ children }: LoomScriptReadTimeContextProviderProps) => children,
);
